/**
 * What is on a tool's screen when nobody is standing at it.
 *
 * Every tool in the fab carries an HMI, and until now every one of them was
 * blank - the mesh existed, `FabScene.updateHmi` existed, and nothing ever
 * called it. A dark rectangle on the front of a running tool is the one detail
 * that reads as "this is a model" rather than "this is a fab", because a real
 * tool's screen is never off.
 *
 * WHAT IT IS FOR, beyond looking right. A student walking the aisle should be
 * able to see that something is running before they decide to walk up to it,
 * the same way a technician scans the row for a tool that has stopped. That is
 * the whole reason the status tower exists on real hardware, and the screen
 * carries the same information with numbers attached.
 *
 * WHERE THE NUMBERS COME FROM. A panel that has been opened at least once is
 * live and can answer for itself through `ToolPanel.hmi()`. A panel that has
 * never been opened does not exist yet - building all twenty-one to paint their
 * screens would construct twenty-one solvers and chart canvases for the sake of
 * a texture - so those fall back to what the floor knows about them without
 * asking: what process they run, and whether the lot in the carrier passed
 * through here.
 */

import type { Tool } from './layout'
import { t } from '../i18n/i18n'
import { MAINTENANCE_TOOL } from './bays'

/** Which of the three lamps on the tower is lit, and what the screen says. */
export interface HmiReadout {
  state: 'idle' | 'running' | 'alarm'
  /** The one thing readable from the middle of the aisle. A number, or a word. */
  headline: string
  /** What that number is, in small caps under it. */
  caption: string
  /** Detail for whoever has walked up. Two fit; more are dropped. */
  rows?: Array<[string, string]>
}

/**
 * What a tool's screen ACTUALLY SAYS, which is not always what its panel
 * reports.
 *
 * The tool that is down for maintenance is the one case: nothing is running
 * inside it, so its panel reports `idle`, while the screen, the tower and
 * the bay's andon all say DOWN - the technician up on the stool inside it
 * is the reason. That substitution used to be made privately inside
 * `paint`, which was fine while a screen was the only thing reading it and
 * became a bug the moment a technician could be asked what their screen
 * says: Tom stood in front of a screen reading "Down" and told students the
 * tool was idle, because the brief (talk/brief.ts) had the raw readout.
 *
 * One helper, so the screen a student reads and the sentence a technician
 * speaks cannot disagree again.
 */
export function displayedReadout(tool: Tool, readout: HmiReadout): { readout: HmiReadout; down: boolean } {
  if (tool.id === MAINTENANCE_TOOL && readout.state === 'idle') {
    return {
      readout: { state: 'idle', headline: t('hmi.Down'), caption: t('hmi.PmInProgress'), rows: readout.rows },
      down: true,
    }
  }
  return { readout, down: false }
}

/**
 * A caption in small caps - unless it is a unit.
 *
 * Case is part of a unit's spelling. Upper-cased, the micro sign becomes a
 * capital Mu, which is drawn as M: EPI-20's growth rate read "MM/MIN",
 * millimetres a minute, on a tool that grows a micrometre in an hour. "mK"
 * becomes megakelvin and "mJ" megajoules by the same road. A caption that
 * carries a unit is left exactly as its panel wrote it; a word ("Rate",
 * "Status") is still set in capitals like the rest of the screen's furniture.
 */
const UNIT = /[µμΩ°²³⁻/%₀₁]|(nm|µm|mm|mK|K|mJ|eV|keV|pA|nA|GHz|MHz|Hz|gf|MPa|ppm|Torr|mbar|dB|fF|W|V|s|h|ISO|Cpk|CD|LER)/
export const capsCaption = (caption: string): string => (UNIT.test(caption) ? caption : caption.toUpperCase())

const W = 320
const H = 208

const BAND = {
  idle: '#7d858f',
  running: '#0ca30c',
  alarm: '#fab219',
} as const

/**
 * One canvas per tool, kept for the life of the session.
 *
 * `FabScene.updateHmi` binds a CanvasTexture to whichever canvas it is first
 * handed and thereafter only flags it dirty, so a single shared scratch canvas
 * would leave every screen in the fab showing whichever tool was painted last.
 */
export class HmiScreens {
  private readonly canvases = new Map<string, HTMLCanvasElement>()

  /**
   * The canvas behind this tool's screen, created on first ask.
   *
   * Public so the scene can bind every screen's CanvasTexture at CONSTRUCTION
   * rather than on first paint: assigning a material's first `map` changes its
   * shader program hash, and doing that when a student happens to walk within
   * reading range would recompile mid-stroll - the same class of hitch the
   * constant light budget exists to prevent. Bound up front, before the
   * prewarm compile, the paints that follow only flag the texture dirty.
   */
  canvasFor(toolId: string): HTMLCanvasElement {
    let canvas = this.canvases.get(toolId)
    if (!canvas) {
      canvas = document.createElement('canvas')
      canvas.width = W
      canvas.height = H
      // A screen nobody has painted yet still reads as a powered-dark
      // display, not a hole - the same ground colour paint() lays down.
      const ctx = canvas.getContext('2d')!
      ctx.fillStyle = '#0b1622'
      ctx.fillRect(0, 0, W, H)
      this.canvases.set(toolId, canvas)
    }
    return canvas
  }

  /** Draw this tool's screen and return the canvas to hand to the scene. */
  paint(tool: Tool, processName: string, readout: HmiReadout): HTMLCanvasElement {
    const canvas = this.canvasFor(tool.id)
    const ctx = canvas.getContext('2d')!

    /*
     * Laid out for GLANCING, not reading.
     *
     * The first version of this put four evenly-weighted rows of small text on
     * the screen, which was legible with your nose against it and an
     * undifferentiated dark smear from anywhere else - which is where a student
     * actually is. A tool screen is read from across an aisle, so it gets one
     * colour band you can identify at any distance, one number large enough to
     * read at several metres, and the detail only for someone who has walked
     * up.
     */
    ctx.fillStyle = '#0b1622'
    ctx.fillRect(0, 0, W, H)

    // The tool down for maintenance says so on its own screen while
    // nothing is running in it - the technician inside it is why.
    const shown = displayedReadout(tool, readout)
    readout = shown.readout
    const down = shown.down

    // Status band, coloured to agree with the light tower above it. Full width
    // and deep, because at twenty metres the colour is the only thing that
    // survives - the same reason the tower exists.
    ctx.fillStyle = down ? '#d8262c' : BAND[readout.state]
    ctx.fillRect(0, 0, W, 58)
    ctx.fillStyle = readout.state === 'alarm' ? '#14181d' : '#ffffff'
    ctx.font = 'bold 30px system-ui, sans-serif'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    ctx.fillText(tool.assetTag, 14, 30)
    ctx.font = 'bold 17px system-ui, sans-serif'
    ctx.textAlign = 'right'
    ctx.fillText(clip(ctx, processName.toUpperCase(), 150), W - 14, 31)

    ctx.textAlign = 'center'
    ctx.fillStyle = '#ffffff'
    ctx.font = 'bold 52px system-ui, sans-serif'
    ctx.fillText(clip(ctx, readout.headline, W - 24), W / 2, 112)
    ctx.fillStyle = '#898781'
    ctx.font = '17px system-ui, sans-serif'
    ctx.fillText(clip(ctx, capsCaption(readout.caption), W - 24), W / 2, 146)

    // Detail for whoever has walked up to it.
    let y = 180
    for (const [label, value] of (readout.rows ?? []).slice(0, 2)) {
      ctx.fillStyle = '#898781'
      ctx.font = '16px system-ui, sans-serif'
      ctx.textAlign = 'left'
      ctx.fillText(clip(ctx, label, 140), 14, y)
      ctx.fillStyle = '#c3c2b7'
      ctx.font = 'bold 18px system-ui, sans-serif'
      ctx.textAlign = 'right'
      ctx.fillText(clip(ctx, value, 150), W - 14, y)
      y += 26
    }

    return canvas
  }
}

/** Trim to fit rather than overrun the bezel. */
function clip(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text
  let out = text
  while (out.length > 1 && ctx.measureText(out + '…').width > maxWidth) out = out.slice(0, -1)
  return out + '…'
}
